import { getRgbaColor, breakpoints, colors } from '@web-for-marketing/react-ui';
import { typographyStyles, formControlFontSizeStyles } from '@web-for-marketing/react-ui';
import { createTheme } from '@mui/material/styles';

export const appTheme = createTheme({
    breakpoints: {
        values: {
            xs: parseInt(breakpoints.xs),
            sm: parseInt(breakpoints.sm),
            md: parseInt(breakpoints.md),
            lg: parseInt(breakpoints.lg),
            xl: parseInt(breakpoints.xl),
        },
    },
    palette: {
        primary: {
            main: colors.core.geotab,
        },
        secondary: {
            main: colors.core.innovation,
        },
        text: {
            primary: colors.core.slate,
            secondary: colors.core.silver,
        },
        background: {
            default: colors.core.snow,
        },
    },
    typography: {
        body1: {
            ...typographyStyles.body1,
            color: colors.core.slate,
        },
        body2: {
            ...typographyStyles.body2,
            color: colors.core.slate,
        },
        subtitle1: {
            ...typographyStyles.header4,
            color: colors.core.slate,
        },
        subtitle2: {
            ...typographyStyles.header3,
            color: colors.core.slate,
            fontWeight: 400,
        },
        caption: {
            fontSize: '1.4rem',
            color: colors.core.slate,
            fontWeight: 400,
        },
    },
    components: {
        MuiLink: {
            defaultProps: {
                underline: 'hover',
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    color: colors.core.slate,
                },
            },
        },
        MuiCollapse: {
            styleOverrides: {
                entered: {
                    zIndex: 9999,
                    transform: 'scaleY(1) translateY(0)',
                    opacity: 1,
                    visibility: 'visible',
                    [`@media (max-width: ${breakpoints.sm})`]: {
                        transform: 'scaleY(1) translateY(0)',
                    },
                    borderTop: `0.1rem solid ${getRgbaColor(colors.core.slate, 0.1)}`,
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    height: 'unset',
                },
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    height: '4rem',
                    width: '4rem',
                },
            },
        },
        MuiSvgIcon: {
            styleOverrides: {
                root: {
                    fontSize: '2.4rem',
                },
                colorAction: {
                    fill: colors.core.slate,
                },
            },
        },
        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    display: 'flex',
                },
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                outlined: {
                    ...formControlFontSizeStyles,
                },
                root: {
                    ...formControlFontSizeStyles,
                },
            },
        },
        MuiFab: {
            styleOverrides: {
                root: {
                    height: '5rem',
                    width: '5rem',
                    color: colors.core.innovation,
                    backgroundColor: colors.core.snow,
                    transition: 'color ease .5s',
                    textTransform: 'none',
                    '&:hover': {
                        color: `${colors.core.geotab} !important`,
                        backgroundColor: colors.core.snow,
                        transition: 'color ease .5s',
                    },
                },
            },
        },
        MuiGrid: {
            styleOverrides: {
                container: {
                    width: '100%',
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontSize: '1.8rem',
                    textTransform: 'none',
                    minWidth: 'unset',
                    letterSpacing: '.05rem',
                    textDecoration: 'none',
                    minHeight: '5rem',
                    boxShadow: 'none !important',
                    lineHeight: 1.4,
                    fontWeight: 500,
                    borderRadius: '0.6rem',
                    transition: 'background-color 500ms ease, border-color 500ms ease, color 500ms ease',
                    [`@media (min-width: ${breakpoints.xl})`]: {
                        fontSize: '2rem',
                    },
                    [`@media (max-width: ${breakpoints.lg})`]: {
                        fontSize: '1.8rem',
                    },
                    [`@media (max-width: ${breakpoints.sm})`]: {
                        fontSize: '1.6rem',
                        width: '100%',
                        padding: '1.7rem 0 1.9rem',
                        textAlign: 'center',
                    },
                    '& .MuiTouchRipple-root': {
                        '@media (prefers-reduced-motion: reduce)': {
                            display: 'none !important',
                        },
                    },
                    '&.Mui-disabled': {
                        color: colors.core.slate,
                        border: `1px solid ${colors.core.smoke}`,
                        backgroundColor: colors.core.smoke,
                    },
                },
                contained: {
                    padding: '1.6rem 2.6rem  !important',
                    backgroundColor: colors.core.innovation,
                    color: colors.core.snow,
                    border: '0.1rem solid',
                    borderColor: colors.core.innovation,
                    '&:hover': {
                        borderColor: colors.core.innovation,
                        color: colors.core.innovation,
                        backgroundColor: colors.core.snow,
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                        '&:focus': {
                            borderColor: colors.core.innovation,
                            color: colors.core.innovation,
                            backgroundColor: colors.core.snow,
                        },
                    },
                    [`@media (max-width: ${breakpoints.md})`]: {
                        textAlign: 'center',
                    },
                },
                containedPrimary: {
                    padding: '1.6rem 2.6rem  !important',
                    backgroundColor: colors.core.innovation,
                    color: colors.core.snow,
                    border: '0.1rem solid',
                    borderColor: colors.core.innovation,
                    '&:hover': {
                        borderColor: colors.core.innovation,
                        color: colors.core.innovation,
                        backgroundColor: colors.core.snow,
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                        '&:focus': {
                            borderColor: colors.core.innovation,
                            color: colors.core.innovation,
                            backgroundColor: colors.core.snow,
                        },
                    },
                    [`@media (max-width: ${breakpoints.md})`]: {
                        textAlign: 'center',
                    },
                },
                containedSecondary: {
                    padding: '1.6rem 2.6rem  !important',
                    backgroundColor: colors.core.snow,
                    color: colors.core.innovation,
                    border: '0.1rem solid',
                    borderColor: colors.core.snow,
                    '&:hover': {
                        borderColor: colors.core.snow,
                        color: colors.core.snow,
                        backgroundColor: colors.core.innovation,
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                        '&:focus': {
                            borderColor: colors.core.snow,
                            color: colors.core.snow,
                            backgroundColor: colors.core.innovation,
                        },
                    },
                    [`@media (max-width: ${breakpoints.md})`]: {
                        textAlign: 'center',
                    },
                },
                outlined: {
                    padding: '1.6rem 2.6rem  !important',
                    backgroundColor: colors.core.snow,
                    color: colors.core.innovation,
                    border: '0.1rem solid',
                    borderColor: colors.core.innovation,
                    '&:hover': {
                        color: colors.core.snow,
                        borderColor: colors.core.innovation,
                        backgroundColor: colors.core.innovation,
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                        '&:focus': {
                            color: colors.core.snow,
                            borderColor: colors.core.innovation,
                            backgroundColor: colors.core.innovation,
                        },
                    },
                    [`@media (max-width: ${breakpoints.md})`]: {
                        textAlign: 'center',
                    },
                },
                outlinedSecondary: {
                    padding: '1.6rem 2.6rem  !important',
                    backgroundColor: getRgbaColor(colors.core.innovation, 0.3),
                    color: colors.core.snow,
                    border: '0.1rem solid',
                    borderColor: colors.core.snow,
                    '&:hover': {
                        backgroundColor: colors.core.innovation,
                        borderColor: colors.core.snow,
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                        '&:focus': {
                            backgroundColor: colors.core.innovation,
                            borderColor: colors.core.snow,
                        },
                    },
                    [`@media (max-width: ${breakpoints.md})`]: {
                        textAlign: 'center',
                    },
                },
                text: {
                    color: colors.core.innovation,
                    display: 'inline-block',
                    padding: '0.6rem 0.8rem !important',
                    transform: 'translateX(-0.6rem)',
                    background: 'none !important',
                    whiteSpace: 'initial',
                    '& > *': {
                        whiteSpace: 'initial',
                    },
                    '&:hover': {
                        color: colors.core.innovationDark,
                        textDecoration: 'underline',
                    },
                    [`@media (max-width: ${breakpoints.sm})`]: {
                        width: 'unset',
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                        '&:focus': {
                            color: colors.core.innovationDark,
                            outline: `1px solid ${colors.core.innovationDark}`,
                        },
                        '&:active': {
                            color: colors.core.innovationDark,
                            outline: 'none',
                        },
                    },
                },
                textSecondary: {
                    color: colors.core.snow,
                    display: 'inline-block',
                    padding: '0.6rem 0.8rem !important',
                    transform: 'translateX(-0.6rem)',
                    backgroundColor: 'unset !important',
                    border: `0.1rem solid ${getRgbaColor(colors.core.snow, 0)}`,
                    '&:hover': {
                        color: colors.core.snow,
                        borderColor: colors.core.snow,
                        backgroundColor: `${getRgbaColor(colors.core.innovation, 0.3)} !important`,
                    },
                    [`@media (max-width: ${breakpoints.sm})`]: {
                        width: 'unset',
                    },
                    '@media (prefers-reduced-motion: reduce)': {
                        '&:focus': {
                            color: colors.core.snow,
                            borderColor: colors.core.snow,
                            backgroundColor: `${getRgbaColor(colors.core.innovation, 0.3)} !important`,
                        },
                    },
                },
            },
        },
        MuiTooltip: {
            styleOverrides: {
                tooltip: {
                    fontSize: '1.4rem',
                    fontWeight: 400,
                    lineHeight: '1.5 !important',
                },
            },
        },
        MuiList: {
            styleOverrides: {
                padding: {
                    paddingTop: 0,
                    paddingBottom: 0,
                },
            },
        },
        MuiDialog: {
            styleOverrides: {
                paper: {
                    [`@media (max-width: ${breakpoints.sm})`]: {
                        margin: '0',
                    },
                },
            },
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    marginTop: '-1rem',
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: {
                    fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    zIndex: '400 !important' as unknown as number,
                },
                elevation24: {
                    boxShadow: `0px 0px 15px ${getRgbaColor(colors.core.deepSpace, 0.5)}`,
                    borderRadius: '10px',
                },
            },
        },
        MuiMobileStepper: {
            styleOverrides: {
                dotActive: {
                    backgroundColor: colors.core.innovation,
                },
                dot: {
                    backgroundColor: colors.core.cloud,
                },
            },
        },
        MuiTablePagination: {
            styleOverrides: {
                input: {
                    fontSize: '1.2rem',
                },
            },
        },
        MuiStep: {
            styleOverrides: {
                root: {
                    flex: 'unset !important',
                    minWidth: '20rem',
                    paddingLeft: '2% !important',
                    paddingRight: '2% !important',
                },
            },
        },
        MuiStepIcon: {
            styleOverrides: {
                root: {
                    fill: colors.core.snow,
                    border: `2px ${colors.core.smoke} solid`,
                    borderRadius: '100%',
                    height: '3.8rem',
                    width: '3.8rem',
                    '&.Mui-active': {
                        borderColor: colors.core.innovation,
                        '& text': {
                            fill: colors.core.geotab,
                        },
                    },
                    text: {
                        fill: colors.core.smoke,
                        fontWeight: 500,
                        fontSize: '0.5em',
                    },
                },
            },
        },
        MuiStepConnector: {
            styleOverrides: {
                root: {
                    display: 'none',
                },
            },
        },
        MuiStepLabel: {
            styleOverrides: {
                label: {
                    fontSize: '1.7rem',
                    letterSpacing: 0,
                    fontWeight: 500,
                    color: colors.core.smoke,
                    marginTop: '2rem',
                },
                root: {
                    '&.Mui-active': {
                        color: `${colors.core.geotab} !important`,
                    },
                },
            },
        },
        MuiStepper: {
            styleOverrides: {
                root: {
                    justifyContent: 'center',
                    width: '100%',
                    position: 'relative',
                    '&::after': {
                        content: '""',
                        width: '96%',
                        position: 'absolute',
                        backgroundColor: colors.core.smoke,
                        height: '2px',
                        top: '25%',
                        left: '2%',
                        zIndex: '-1',
                    },
                },
            },
        },
        MuiInput: {
            styleOverrides: {
                underline: {
                    borderColor: `${colors.core.geotab} !important`,
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        backgroundColor: getRgbaColor(colors.core.deepSpace, 0.03),
                    },
                    '&:focus': {
                        backgroundColor: getRgbaColor(colors.core.deepSpace, 0.1),
                    },
                },
            },
        },
        MuiSnackbar: {
            styleOverrides: {
                anchorOriginTopCenter: {
                    top: '0 !important',
                },
            },
        },
        MuiFormControl: {
            styleOverrides: {
                root: {
                    ...formControlFontSizeStyles,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    fontSize: 'inherit',
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    fontSize: '1.4rem',
                    [`@media (min-width: ${breakpoints.xl})`]: {
                        fontSize: '1.6rem',
                    },
                    [`@media (max-width: ${breakpoints.sm})`]: {
                        fontSize: '1.2rem',
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    fontSize: '1.5rem !important',
                    padding: '1.5rem',
                },
            },
        },
    },
});
